import React, { useState } from "react";
import { useParams } from "react-router-dom";
import styled from "@emotion/styled";
import { Body2 } from "@material/react-typography";
import TitledCard from "~/components/titled-card";
import Button from "~/components/button";
import { useSnackBar } from "~/components/snackbar";
import { useBreadcrumbs } from "~/main-layout/BreadcrumbProvider";
import { Table, TableRow, TableCell } from "~/table";
import { CommandAuthorization, PriceClassification } from "~/gql/types2";
import { useUser } from "~/Auth/useUser";
import { useProducts } from "./ProductsProvider";
import { RenameProductDialog } from "./RenameProductDialog";
import { AddProductRateDialog } from "./AddProductRateDialog";
import {
  amountSource,
  classificationLabel,
  currency,
  isExternallySourced,
  overrideRates,
} from "./shared";

type ProductParams = {
  id: string;
};

const Detail = styled(Body2)`
  margin: 0 0 4px;
`;

export const ProductPage: React.FC = () => {
  const { id } = useParams<ProductParams>();

  const { products, saving, addProductRate, renameProduct } = useProducts();
  const { renaming, addingRate } = saving;

  // The provider withholds children until the catalog has loaded, so a miss
  // here is a genuine miss -- not a race with an in-flight query.
  const product = products.find((x) => `${x.id}` === id);

  useBreadcrumbs(
    [{ text: "Products", to: "/products" }, { text: product?.name ?? "" }],
    [product?.name]
  );

  const addAlert = useSnackBar();
  const { commands } = useUser();

  const canAddRate = commands.has(CommandAuthorization.AddProductRateCommand);
  const canRename = commands.has(CommandAuthorization.RenameProductCommand);

  const [rateDialogOpen, setRateDialogOpen] = useState(false);
  const [renameDialogOpen, setRenameDialogOpen] = useState(false);

  if (!product) {
    return <TitledCard title="Product not found" />;
  }

  const isFixed = product.classification === PriceClassification.Fixed;
  const isPerDiem = product.classification === PriceClassification.PerDiem;

  // A per diem product's non-null rates are overrides of the per diem table's
  // amount, not catalogued prices; its null sentinel is never listed.
  const listedRates = isPerDiem ? overrideRates(product) : product.rates;

  // Fixed prices and per diem overrides are both worth listing and adding to.
  // A variable or employee-rate product carries only its sentinel, which is an
  // implementation detail rather than a price.
  const listsRates = isFixed || isPerDiem;

  const announce = (message: string) =>
    addAlert({ key: `${Math.random()}`, message, isSuccess: true });

  const rateActions =
    listsRates && canAddRate ? (
      <Button
        primary
        disabled={addingRate}
        onClick={() => setRateDialogOpen(true)}
      >
        {isPerDiem ? "Add Override" : "Add Rate"}
      </Button>
    ) : undefined;

  return (
    <>
      <TitledCard
        title={product.name}
        subtitle={`${product.unit}`}
        actions={
          // An externally-sourced product's name is how it is identified, so it
          // is not renameable whatever the user is authorized to do. The domain
          // rejects the rename too -- this just does not offer it.
          canRename && !isExternallySourced(product.classification) ? (
            <Button
              icon="edit"
              title="Rename product"
              aria-label="Rename product"
              disabled={renaming}
              onClick={() => setRenameDialogOpen(true)}
            />
          ) : undefined
        }
      >
        <Detail>{classificationLabel[product.classification]}</Detail>
        <Detail>{amountSource[product.classification]}</Detail>
        <Detail>
          Price classification and unit are fixed for the life of the product.
        </Detail>
      </TitledCard>

      {listsRates ? (
        <TitledCard
          // A per diem product's rates are overrides, not catalogued prices --
          // titled so the distinction is on the page, not just in the data.
          title={isPerDiem ? "Rate Overrides" : "Rates"}
          subtitle={
            isPerDiem
              ? "Each overrides the per diem table's amount for the rate sheet that uses it."
              : undefined
          }
          actions={rateActions}
          isEmpty={listedRates.length === 0}
          emptyMessage={isPerDiem ? "No overrides yet." : "No rates yet."}
        >
          <Table columnWidths="max-content" columnCount={1}>
            {listedRates.map((rate) => (
              <TableRow key={rate.id}>
                {/* `text` is what wraps the cell in Body2 -- passing children
                    instead drops to a bare div with no typography. */}
                <TableCell text={currency(rate.amount)} />
              </TableRow>
            ))}
          </Table>
        </TitledCard>
      ) : (
        // A variable or employee-rate product does carry a single null-amount
        // rate, but that is a sentinel rather than a price: say why there is
        // nothing to manage instead of silently omitting the card.
        <TitledCard
          title="Rates"
          isEmpty
          emptyMessage={
            product.classification === PriceClassification.EmployeeRate
              ? "No rates can be defined for employee-rate products. Amounts come from the employee rate table, by employee and effective date."
              : "No rates can be defined for variable-price products. Amounts are governed by receipt values, invoice amounts, etc., and can't be constrained by pre-defined rates."
          }
        />
      )}

      {renameDialogOpen && (
        <RenameProductDialog
          product={product}
          open
          loading={renaming}
          onCancel={() => setRenameDialogOpen(false)}
          onRename={(name) => renameProduct({ productId: product.id, name })}
          onSuccess={() => {
            announce("Renamed Product");
            setRenameDialogOpen(false);
          }}
        />
      )}

      {rateDialogOpen && (
        <AddProductRateDialog
          product={product}
          open
          loading={addingRate}
          onCancel={() => setRateDialogOpen(false)}
          onAddRate={(amount) =>
            addProductRate({ productId: product.id, amount })
          }
          onSuccess={() => {
            announce("Added Product Rate");
            setRateDialogOpen(false);
          }}
        />
      )}
    </>
  );
};
